import { MiniatureOptions, MiniatureType } from "../classes/Miniature";

type OrcUnitTemplate = Omit<
  MiniatureOptions,
  "unitsKilled" | "damageDealt" | "killCount"
>;

const DEFAULT_POSITION: [number, number] = [0, 0];

/**
 * The roster is ordered to match the stable catalogue IDs in army/catalog.ts.
 * Core troops offer inexpensive, specialised choices while named characters
 * are deliberately stronger (and pay for that strength in points).
 */
export const orcUnits: OrcUnitTemplate[] = [
  {
    name: "Brog Ironfist",
    description: "A legendary chieftain who crushes armour with his great maul.",
    size: { x: 1, y: 15, z: 30 },
    position: DEFAULT_POSITION,
    bearing: 0,
    speed: 4,
    hitpoints: 24,
    armour: 4,
    meleeAttack: 5,
    rangeAttack: 6,
    weapons: [
      {
        name: "Great Maul",
        description: "A massive two-handed hammer.",
        damage: 6,
        range: 1,
      },
      {
        name: "Brass Knuckles",
        description: "A fast backup weapon for close scrapping.",
        damage: 3,
        range: 0,
      },
    ],
    type: MiniatureType.CHARACTER,
    image: "assets/character_1.png",
  },
  {
    name: "Scrapling Rabble",
    description: "Cheap, nervous scraplings armed with battered slingshots.",
    size: { x: 1, y: 10, z: 20 },
    position: DEFAULT_POSITION,
    bearing: 0,
    speed: 6,
    hitpoints: 4,
    armour: 0,
    meleeAttack: 1,
    rangeAttack: 4,
    weapons: [
      {
        name: "Slingshot",
        description: "Crude, weak, but better than fighting up close.",
        damage: 1,
        range: 8,
      },
    ],
    type: MiniatureType.INFANTRY,
  },
  {
    name: "Rivetback Rider",
    description: "A fast shock unit that trades durability for speed.",
    size: { x: 2, y: 30, z: 50 },
    position: DEFAULT_POSITION,
    bearing: 0,
    speed: 15,
    hitpoints: 7,
    armour: 2,
    meleeAttack: 3,
    rangeAttack: 4,
    weapons: [
      {
        name: "Chainsaw",
        description: "A loud and vicious chainsaw.",
        damage: 4,
        range: 1,
      },
      {
        name: "Pistol",
        description: "A compact sidearm fired from the saddle.",
        damage: 2,
        range: 6,
      },
    ],
    type: MiniatureType.VEHICLE,
    image: "assets/biker_1.png",
  },
  {
    name: "Rivetmaw Outrider",
    description: "A notorious biker boss whose power klaw can tear through tanks.",
    type: MiniatureType.CHARACTER,
    mobilityProfileId: "vehicle",
    size: { x: 2, y: 2, z: 3 },
    position: DEFAULT_POSITION,
    bearing: 0,
    speed: 16,
    meleeAttack: 6,
    rangeAttack: 4,
    armour: 4,
    hitpoints: 20,
    weapons: [
      {
        name: "Power Klaw",
        description: "A powered claw made for ripping open heavy armour.",
        damage: 7,
        range: 1,
      },
      {
        name: "Twin Dakkaguns",
        description: "Paired short-ranged guns mounted on the warbike.",
        damage: 4,
        range: 8,
      },
    ],
    image: "assets/biker_1.png",
  },
  {
    name: "Skabrot the Terrible",
    description: "A heavily armoured warboss whose snazgun controls open ground.",
    type: MiniatureType.CHARACTER,
    size: { x: 2, y: 2, z: 3 },
    position: DEFAULT_POSITION,
    bearing: 0,
    speed: 10,
    meleeAttack: 4,
    rangeAttack: 2,
    armour: 5,
    hitpoints: 24,
    weapons: [
      {
        name: "Snazgun",
        description: "An accurate rapid-fire gun built from stolen technology.",
        damage: 5,
        range: 14,
      },
      {
        name: "Boss Choppa",
        description: "A brutal cleaver for enemies who get too close.",
        damage: 4,
        range: 1,
      },
    ],
  },
  {
    name: "Varkul the Invader",
    description: "A relentless conqueror carrying a devastating mega-shoota.",
    type: MiniatureType.CHARACTER,
    size: { x: 2, y: 2, z: 3 },
    position: DEFAULT_POSITION,
    bearing: 0,
    speed: 9,
    meleeAttack: 5,
    rangeAttack: 3,
    armour: 4,
    hitpoints: 25,
    weapons: [
      {
        name: "Mega-Shoota",
        description: "A heavy gun that blankets the battlefield in bullets.",
        damage: 6,
        range: 13,
      },
      {
        name: "Invader's Choppa",
        description: "A trophy blade taken on an earlier conquest.",
        damage: 5,
        range: 1,
      },
    ],
  },
  {
    name: "Krog the Despoiler",
    description: "A terrifying duellist who hunts the toughest enemy on the field.",
    type: MiniatureType.CHARACTER,
    size: { x: 1, y: 2, z: 1.5 },
    position: DEFAULT_POSITION,
    bearing: 0,
    speed: 6,
    meleeAttack: 6,
    rangeAttack: 3,
    armour: 4,
    hitpoints: 23,
    weapons: [
      {
        name: "Despoiler Choppa",
        description: "A massive jagged blade wielded with deadly precision.",
        damage: 7,
        range: 1,
      },
      {
        name: "Big Shoota",
        description: "A heavy gun used to soften up distant prey.",
        damage: 5,
        range: 12,
      },
    ],
  },
  {
    name: "Scrapjaw Bruiser",
    description: "Reliable close-combat infantry with a cleaver and bad attitude.",
    type: MiniatureType.INFANTRY,
    size: { x: 1, y: 12, z: 24 },
    position: DEFAULT_POSITION,
    bearing: 0,
    speed: 5,
    meleeAttack: 3,
    rangeAttack: 6,
    armour: 2,
    hitpoints: 8,
    weapons: [
      {
        name: "Choppa",
        description: "A heavy blade made for close combat.",
        damage: 3,
        range: 1,
      },
    ],
  },
  {
    name: "Boltspitter",
    description: "Line infantry that trades some toughness for ranged pressure.",
    type: MiniatureType.INFANTRY,
    size: { x: 1, y: 12, z: 24 },
    position: DEFAULT_POSITION,
    bearing: 0,
    speed: 5,
    meleeAttack: 2,
    rangeAttack: 4,
    armour: 1,
    hitpoints: 7,
    weapons: [
      {
        name: "Shoota",
        description: "A noisy automatic weapon with respectable reach.",
        damage: 3,
        range: 10,
      },
    ],
  },
  {
    name: "Cinder Raider",
    description: "An assault specialist carrying a vicious short-ranged burner.",
    type: MiniatureType.INFANTRY,
    size: { x: 1, y: 12, z: 24 },
    position: DEFAULT_POSITION,
    bearing: 0,
    speed: 5,
    meleeAttack: 2,
    rangeAttack: 3,
    armour: 1,
    hitpoints: 7,
    weapons: [
      {
        name: "Burna",
        description: "A short-ranged gout of scavenged fuel and flame.",
        damage: 4,
        range: 6,
      },
      {
        name: "Cutting Torch",
        description: "The burna's blade-like close-combat setting.",
        damage: 2,
        range: 1,
      },
    ],
  },
  {
    name: "Scrapbolt Cannon",
    description: "A slow artillery platform with exceptional range and a fragile crew.",
    type: MiniatureType.VEHICLE,
    size: { x: 2, y: 20, z: 18 },
    position: DEFAULT_POSITION,
    bearing: 0,
    speed: 2,
    meleeAttack: 0,
    rangeAttack: 3,
    armour: 3,
    hitpoints: 10,
    weapons: [
      {
        name: "Kustom Kannon",
        description: "An unstable cannon that hits hard from far away.",
        damage: 6,
        range: 18,
      },
    ],
  },
  {
    name: "Rivetclaw Walker",
    description: "A lumbering armoured walker built to dominate close combat.",
    type: MiniatureType.ROBOT,
    mobilityProfileId: "vehicle",
    size: { x: 3, y: 24, z: 40 },
    position: DEFAULT_POSITION,
    bearing: 0,
    speed: 4,
    meleeAttack: 5,
    rangeAttack: 5,
    armour: 5,
    hitpoints: 18,
    weapons: [
      {
        name: "Dread Klaw",
        description: "A hydraulic claw capable of crushing vehicles.",
        damage: 6,
        range: 1,
      },
      {
        name: "Big Shoota",
        description: "A supporting gun for targets beyond claw reach.",
        damage: 3,
        range: 10,
      },
    ],
  },
];
